/**
 * Seite „Werkzeuge" der Sektion „Anfos & Umsatz" (P5 Kern, Plan 2026-10-27-p4-hebel-p5-kern Paket B): die Prüf- und
 * Nachschlage-Tabs der alten /webinar-auswertungen — Webinar-Zuordnungen (Tab 1), Affiliate-Gruppierungen (2),
 * Design-A/B (5), Formelblatt (6). Die Komponenten sind die alten (wiederverwendet), die Daten kommen über
 * /api/auswertungen/werkzeuge/* aus denselben Service-Funktionen (`services/webinarWerkzeuge.ts`) — Gruppe
 * fit_products. Reiter in der URL (`?tab=zuordnungen|affiliate|design-ab|formeln`), Zeitraum `startDate`/`endDate`
 * (Standard 3 Monate bis heute, wie der alte Sidebar-Link; Design-A/B ab Teststart 01.08.2026 wie der alte Endpunkt).
 * Dazu Links auf die FIT-Werkzeuge, die bleiben, wo sie sind.
 */
import { FC, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, Link, Tab, Tabs, Typography, useTheme } from '@mui/material';
import moment from 'moment';
import ChapterPage from '../../../components/auswertungen/ChapterPage';
import ChartCard from '../../../components/auswertungen/ChartCard';
import {
  fetchWerkzeugAffiliate,
  fetchWerkzeugDesignAb,
  fetchWerkzeugZuordnungen,
} from '../../../helpers/bestellungenData';
import { germanNow } from '../../../helpers/germanTime';
import type {
  DesignAbData,
  WebinarAffiliateGroupingOverview,
  WebinarOrderMappingData,
} from '../../../helpers/webinarStatisticsData';
import { tokens } from '../../../theme';
import AffiliateGroupingsTab from '../../webinar-statistics/AffiliateGroupingsTab';
import DesignAbTab from '../../webinar-statistics/DesignAbTab';
import KpiFormulaSheet from '../../webinar-statistics/kpi/KpiFormulaSheet';
import OrderMappingsTab from '../../webinar-statistics/OrderMappingsTab';
import DatumFeld, { datumGueltig } from '../bestellungen/DatumFeld';

/** Reiter in Reihenfolge. Die Schlüssel stehen auch in `../umleitungsTabelle.ts` (`ALT_TAB_ZU_WERKZEUG`) — beide ändern. */
export const WERKZEUG_TABS = [
  { key: 'zuordnungen', label: 'Webinar-Zuordnungen', mitZeitraum: true },
  { key: 'affiliate', label: 'Affiliate-Gruppierungen', mitZeitraum: false },
  { key: 'design-ab', label: 'Design-A/B', mitZeitraum: true },
  { key: 'formeln', label: 'Formelblatt', mitZeitraum: false },
] as const;
type TabKey = (typeof WERKZEUG_TABS)[number]['key'];

const FIT_WERKZEUGE: { to: string; label: string; text: string }[] = [
  { to: '/plx-orders/config', label: 'Anfo-Konfiguration', text: 'Werbeaktionen (WKZ) mit Preis, Testphase und Bezugsart pflegen.' },
  { to: '/plx-orders/products', label: 'Produkte verwalten', text: 'Produkte, Namen und Anfo-Werte je Produkt.' },
  { to: '/plx-orders/groupings', label: 'Anfo-Gruppierungen', text: 'Kanal- und Kennungsgruppen der Bestellungen nachschlagen.' },
  { to: '/plx-orders/px-import', label: 'PX-Data Import', text: 'Bestellungen aus dem PX-Data-Export einspielen und prüfen.' },
];

/**
 * Gültiger Tag ab 2020-01-01 (wie das Backend, `routes/auswertungen.ts` `werkzeugZeitraum`): ohne Untergrenze war beim
 * Tippen der Jahreszahl „0002-…" gültig und die Zuordnungs-Abfrage lief über den ganzen Bestand (Review 27.10.2026).
 * Die Felder sind `DatumFeld` (Entwurf im Feld, nur gültige Tage in die URL).
 */
const gueltig = (v: string | null): v is string => datumGueltig(v);
/** Standard-Anfang des Reiters Design-A/B: Start des Tests (der alte Endpunkt zeigte ohne Zeitraum „ab 01.08.“). */
const DESIGN_AB_AB = '2026-08-01';

interface Lade<T> {
  key: string;
  data: T | null;
  fehler: string | null;
}

/** Lädt bei jedem neuen Schlüssel; zeigt nur die Antwort zum aktuellen Schlüssel (kein Rennen bei schnellem Wechsel). */
function useLadenMit<T>(key: string | null, laden: () => Promise<T>): { data: T | null; fehler: string | null; laedt: boolean } {
  const [stand, setStand] = useState<Lade<T> | null>(null);
  useEffect(() => {
    if (key === null) return undefined;
    let abgebrochen = false;
    laden()
      .then((data) => !abgebrochen && setStand({ key, data, fehler: null }))
      .catch((e: unknown) => !abgebrochen && setStand({ key, data: null, fehler: e instanceof Error ? e.message : String(e) }));
    return () => {
      abgebrochen = true;
    };
    // laden hängt nur an key (der Schlüssel enthält alle Parameter).
  }, [key]);
  const aktuell = stand && stand.key === key ? stand : null;
  return { data: aktuell?.data ?? null, fehler: aktuell?.fehler ?? null, laedt: key !== null && !aktuell };
}

const Werkzeuge: FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [params, setParams] = useSearchParams();
  const heute = useMemo(() => germanNow().format('YYYY-MM-DD'), []);
  const tab: TabKey = (WERKZEUG_TABS.find((t) => t.key === params.get('tab'))?.key ?? 'zuordnungen') as TabKey;
  const tabInfo = WERKZEUG_TABS.find((t) => t.key === tab)!;
  const rohBis = params.get('endDate');
  const rohVon = params.get('startDate');
  const bis = gueltig(rohBis) ? rohBis : heute;
  const dreiMonate = moment(bis).subtract(3, 'months').format('YYYY-MM-DD');
  const standardVon = tab === 'design-ab' && DESIGN_AB_AB <= bis ? DESIGN_AB_AB : dreiMonate;
  const von = gueltig(rohVon) && rohVon <= bis ? rohVon : standardVon;
  const [methode, setMethode] = useState<string>(params.get('mappingMethod') ?? 'all');

  const setze = (aenderung: Record<string, string | null>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(aenderung)) {
          if (v === null) next.delete(k);
          else next.set(k, v);
        }
        return next;
      },
      { replace: false },
    );

  const zuordnungen = useLadenMit<WebinarOrderMappingData[]>(tab === 'zuordnungen' ? `z|${von}|${bis}|${methode}` : null, () =>
    fetchWerkzeugZuordnungen(von, bis, methode),
  );
  const affiliate = useLadenMit<WebinarAffiliateGroupingOverview>(tab === 'affiliate' ? 'a' : null, fetchWerkzeugAffiliate);
  const designAb = useLadenMit<DesignAbData>(tab === 'design-ab' ? `d|${von}|${bis}` : null, () => fetchWerkzeugDesignAb(von, bis));

  const status = (s: { fehler: string | null; laedt: boolean }) =>
    s.fehler ? (
      <Alert severity="warning">Konnte nicht geladen werden: {s.fehler}</Alert>
    ) : s.laedt ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    ) : null;

  return (
    <ChapterPage
      title="Werkzeuge"
      question="Welche Bestellung gehört zu welchem Webinar, wie werden Kennungen gruppiert — und wie rechnen die Webinar-Kennzahlen?"
      intro={
        <Typography component="span" variant="body2" sx={{ color: 'text.secondary', display: 'block', mt: 1, maxWidth: '75ch' }}>
          Kein Bericht — Werkzeuge fürs Team, um Zuordnungen zu prüfen und zu pflegen. Zahlen für Entscheidungen stehen in den
          Kapiteln. Bisher Reiter der Seite „Webinar Auswertungen“, Inhalt und Zahlen unverändert.
        </Typography>
      }
      standQuellen={[{ key: 'plx_orders', label: 'Bestellsystem (PL-X)' }]}
    >
      <Tabs
        value={tab}
        onChange={(_e, k: TabKey) => setze({ tab: k === 'zuordnungen' ? null : k })}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        // Gewählter Reiter in Textfarbe: primary.main ist in diesem Theme fast schwarz und verschwand im Dunkelmodus.
        sx={{
          mb: 2,
          maxWidth: '100%',
          '& .MuiTab-root.Mui-selected': { color: 'text.primary', fontWeight: 700 },
          '& .MuiTabs-indicator': { backgroundColor: 'text.primary' },
        }}
      >
        {WERKZEUG_TABS.map((t) => (
          <Tab key={t.key} value={t.key} label={t.label} />
        ))}
      </Tabs>

      {tabInfo.mitZeitraum && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <DatumFeld
            label={tab === 'design-ab' ? 'Webinare ab' : 'Bestellt ab'}
            value={von}
            max={bis}
            width={170}
            onGueltig={(v) => setze({ startDate: v, endDate: bis })}
          />
          <DatumFeld label="bis" value={bis} min={von} width={170} onGueltig={(v) => setze({ startDate: von, endDate: v })} />
          {tab === 'zuordnungen' && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Nur Bestellungen von Adressen mit Finanztrends-Eintritt, wie bisher. Zuordnungen ohne Kennung stehen im
              Webinar-Detail (Reiter „Zuordnung der Bestellungen“).
            </Typography>
          )}
        </Box>
      )}

      {/* Die alten Tab-Komponenten haben feste Breiten (Tabellen, Auswahl 250 px): auf dem Handy scrollen sie in sich. */}
      <Box sx={{ minWidth: 0, maxWidth: '100%', overflowX: 'auto', pt: 1 }}>
      {tab === 'zuordnungen' &&
        (status(zuordnungen) ?? (
          <OrderMappingsTab
            data={zuordnungen.data ?? []}
            loading={zuordnungen.laedt}
            colors={colors}
            selectedMappingMethod={methode}
            onMappingMethodChange={(m) => {
              setMethode(m);
              setze({ mappingMethod: m === 'all' ? null : m });
            }}
          />
        ))}
      {tab === 'affiliate' && (status(affiliate) ?? <AffiliateGroupingsTab colors={colors} data={affiliate.data} />)}
      {tab === 'design-ab' && (status(designAb) ?? <DesignAbTab colors={colors} data={designAb.data} />)}
      {tab === 'formeln' && <KpiFormulaSheet />}
      </Box>

      <Box sx={{ mt: 4 }}>
        <ChartCard
          title="Weitere Werkzeuge unter „FIT Produkte“"
          howToRead="Diese Pflegeseiten bleiben, wo sie sind — auch die Links in Hinweis-Mails zeigen dorthin."
          source="Dashboard"
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(240px, 100%), 1fr))', gap: 2 }}>
            {FIT_WERKZEUGE.map((w) => (
              <Box key={w.to} sx={{ border: 1, borderColor: 'divider', borderRadius: '8px', px: 2, py: 1.5 }}>
                <Link component={RouterLink} to={w.to} underline="hover" sx={{ fontWeight: 600 }}>
                  {w.label}
                </Link>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, lineHeight: 1.5 }}>
                  {w.text}
                </Typography>
              </Box>
            ))}
          </Box>
        </ChartCard>
      </Box>
    </ChapterPage>
  );
};

export default Werkzeuge;
